import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Login from './Login';
import Dashboard from './Dashboard';
import Orders from './Orders';
import Products from './Products';
import Merchants from './Merchants';
import Payments from './Payments';
import Categories from './Categories';
import PinnedReports from './PinnedReports';
import Chat from './Chat';
import AiAnalyticsAssistant from './AiAnalyticsAssistant';
import Sidebar from './Sidebar';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [activePage, setActivePage] = useState('dashboard');
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);

  const handleLogout = () => {
    localStorage.removeItem('superadmin_auth');
    setIsAuthenticated(false);
    setUser(null);
    setActivePage('dashboard');
  };

  // Setup axios interceptor for session expiration
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check if error indicates session expiration or invalid token
        const isSessionError = 
          (error.response && error.response.status === 401) || // Unauthorized
          (error.response && error.response.status === 403) || // Forbidden
          (error.response && error.response.data && 
           (error.response.data.error?.toLowerCase().includes('invalid token') ||
            error.response.data.error?.toLowerCase().includes('token expired') ||
            error.response.data.error?.toLowerCase().includes('unauthorized') ||
            error.response.data.message?.toLowerCase().includes('invalid token') ||
            error.response.data.message?.toLowerCase().includes('token expired')));

        if (isSessionError) {
          // Clear authentication and redirect to login immediately
          localStorage.removeItem('superadmin_auth');
          setIsAuthenticated(false);
          setUser(null);
          setActivePage('dashboard');
          
          // Return a never-resolving promise to prevent error propagation
          // This ensures components don't catch and display the error
          return new Promise(() => {});
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Check authentication on mount
  useEffect(() => {
    const authData = localStorage.getItem('superadmin_auth');
    if (authData) {
      try {
        const auth = JSON.parse(authData);
        if (auth.token && auth.user) {
          setIsAuthenticated(true);
          setUser(auth.user);
        } else {
          localStorage.removeItem('superadmin_auth');
        }
      } catch (e) {
        localStorage.removeItem('superadmin_auth');
      }
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogin = (success, userData) => {
    setIsAuthenticated(success);
    if (success && userData) {
      setUser(userData);
      setActivePage('dashboard');
    }
  };

  const handleAccountLogout = () => {
    setIsAccountMenuOpen(false);
    handleLogout();
  };

  const accountEmail = user?.email || 'superadmin@ecom.com';
  const accountName = user?.name || 'Super Admin';


  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  const renderPage = () => {
    switch (activePage) {
      case 'ai-analytics-assistant':
        return <AiAnalyticsAssistant setActivePage={setActivePage} />;
      case 'dashboard':
        return <Dashboard setActivePage={setActivePage} />;
      case 'orders':
        return <Orders setActivePage={setActivePage} />;
      case 'products':
        return <Products setActivePage={setActivePage} />;
      case 'merchants':
        return <Merchants setActivePage={setActivePage} />;
      case 'payments':
        return <Payments setActivePage={setActivePage} />;
      case 'categories':
        return <Categories setActivePage={setActivePage} />;
      case 'pinned-reports':
        return <PinnedReports setActivePage={setActivePage} />;
      case 'chat':
        return <Chat setActivePage={setActivePage} />;
      default:
        return <Dashboard setActivePage={setActivePage} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar activePage={activePage} setActivePage={setActivePage} />
      
      <div className="flex-1 flex flex-col">
        <header className="bg-white shadow-sm border-b px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
              <p className="text-sm text-gray-500">Manage your e-commerce platform</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setActivePage('ai-analytics-assistant')}
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                title="AI Analytics Assistant"
              >
                <span className="text-base">🧠</span>
                AI Analytics Assistant
              </button>
              <div className="relative" ref={accountMenuRef}>
                <button
                  onClick={() => setIsAccountMenuOpen((prev) => !prev)}
                  className="flex items-center gap-3 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  title="Account menu"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white text-sm font-semibold">
                    {accountName.charAt(0).toUpperCase()}
                  </div>
                  <div className="hidden sm:flex flex-col items-start leading-tight">
                    <span className="text-sm font-medium text-gray-900">{accountName}</span>
                  </div>
                  <svg
                    className={`w-4 h-4 text-gray-500 transition-transform ${isAccountMenuOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isAccountMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 rounded-xl border border-gray-200 bg-white shadow-lg z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">{accountName}</p>
                      <p className="text-xs text-gray-500 mt-1">{accountEmail}</p>
                    </div>
                    <button
                      onClick={handleAccountLogout}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          {renderPage()}
        </main>
      </div>
    </div>
  );
}

export default App;
